import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import uniq from "lodash/uniq";
import GalleryCarousel from "../gallery-carousel";
import PicturesGrid from "../pictures-grid";
import GalleryProjectModal from "../gallery-project-modal";
import GalleryPhotoSlider from "../gallery-photo-slider";
import { getClient } from "lib/sanity.server";
import { GET_SPACE_ATTRIBUTES } from "data";
import segmentUtils from "utils/segmentUtils";
import useUser from "hooks/useUser";
import useGeolocationInfo from "hooks/useGeolocationInfo";

const isGalleryPage = (path) => !!path?.startsWith("/gallery");

const navigate = (router) => {
  const query = {};
  const pathname = isGalleryPage(router.pathname) ? router.pathname : router.asPath;
  if (router.query.version) query.version = router.query.version;
  if (router.query.project_id) query.project_id = router.query.project_id;
  if (router.query.slider_photo_id) query.slider_photo_id = router.query.slider_photo_id;
  if (router.query.scroll_to_photo_id) query.scroll_to_photo_id = router.query.scroll_to_photo_id;
  router.push({ pathname, query }, undefined, { shallow: true });
};

const Gallery = ({ data = {} }) => {
  const router = useRouter();
  const { user } = useUser();
  const { version } = router.query;
  const { pictures, paginatedPictures, title } = data;

  const [rooms, setRooms] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [sliderIndex, setSliderIndex] = useState(-1);
  const [modalActivePic, setModalActivePic] = useState(null);
  const [allProjectPics, setAllProjectPics] = useState([]);
  const [selectedProjectPics, setSelectedProjectPics] = useState([]);
  const [selectedProjectPicsByRoom, setSelectedProjectPicsByRoom] = useState([]);
  // store modal-content-node to handle scroll to photo feature
  const [modalScrollableNode, setModalScrollableNode] = useState(null);
  const { geoLocationInfo } = useGeolocationInfo();

  // state util
  useEffect(() => {
    getClient(false)
      .fetch(GET_SPACE_ATTRIBUTES)
      .then((data) => {
        if (data?.spaceAttributes?.length > 0) {
          setSpaces(data.spaceAttributes.flatMap((attr) => attr.internalSpace).filter(Boolean));
        }
      })
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    setAllProjectPics(
      pictures?.length
        ? Object.values(
            pictures.reduce((acc, cur) => {
              if (cur?.project?._id) {
                if (!acc[cur.project._id]) acc[cur.project._id] = [];
                acc[cur.project._id].push(cur);
              }
              return acc;
            }, {})
          )
        : []
    );
  }, [pictures]);

  const resetState = useCallback((allImages, allSpaces, picture) => {
    if (!allImages?.length || !picture) return;

    const picsMap = {};
    const projectId = picture.project?._id;
    const pics = allImages.filter((p) => p?.project?._id === projectId);
    const picRooms = [];
    for (const pic of pics) {
      pic?.spaces?.forEach((picSpace) => {
        const { _key } = picSpace || {};
        const currentSpace = allSpaces?.find((space) => String(space.id) === _key);
        if (currentSpace && _key) {
          const { id, spaceName } = currentSpace;
          const idStr = String(id);
          if (!picsMap[idStr]) {
            picsMap[idStr] = [];
            picRooms.push({ _key: idStr, spaceName });
          }
          picsMap[idStr].push(pic);
        }
      });
    }

    setSelectedProjectPics(pics);
    setSelectedProjectPicsByRoom(picsMap);
    setRooms(picRooms);

    return { projectPics: pics, projectId };
  }, []);

  // open corresponding modal based on route
  useEffect(() => {
    if (!pictures?.length) return;

    let projectId, projectSlug, sliderPhotoId;
    if (isGalleryPage(router.asPath)) {
      const paths = router.asPath.split("?")[0].split("/").filter(Boolean);
      if (paths[1] === "project") {
        projectSlug = paths[2];
        sliderPhotoId = paths[4];
      }
    } else {
      projectId = router.query.project_id;
      sliderPhotoId = router.query.slider_photo_id;
    }

    // modal active photo
    let modalPhotoId = router.query.scroll_to_photo_id || sliderPhotoId;
    if (projectSlug && !modalPhotoId) {
      // fallback to first image
      modalPhotoId = pictures.find(({ project }) => project?.slug === projectSlug)?._id;
    }
    const modalPhoto = modalPhotoId ? pictures.find((p) => p?._id === modalPhotoId) : null;

    if (sliderPhotoId) {
      const sliderPhoto = pictures.find((p) => p?._id === sliderPhotoId);
      const { projectPics: pics } = resetState(pictures, spaces, sliderPhoto);
      setModalActivePic(modalPhoto);
      setSliderIndex(pics.findIndex((p) => sliderPhoto._id === p._id));
    } else {
      setModalActivePic(modalPhoto);
      setSliderIndex(-1);

      if (projectId || projectSlug) {
        // fallback to first image of the project
        resetState(
          pictures,
          spaces,
          modalPhoto ??
            pictures.find(({ project }) => (projectId ? project?._id === projectId : project?.slug === projectSlug))
        );
      }
    }
  }, [pictures, spaces, router.asPath]);

  // modal ref
  const mRef = useCallback(
    (node) => {
      if (node?.parentNode) {
        setModalScrollableNode(node.parentNode);
      }
    },
    [modalActivePic]
  );

  // modal util
  const closeModal = useCallback(() => {
    if (version) router.query.version = version;
    router.query.scroll_to_photo_id = undefined;

    if (router.query.back) {
      const redirectUrl = router.query.back === "home" ? "" : router.query.back;
      return router.push(`/${redirectUrl}`);
    } else if (isGalleryPage(router.asPath)) {
      router.pathname = "/gallery";
    } else {
      router.query.project_id = undefined;
      router.query.slider_photo_id = undefined;
    }
    navigate(router);
  }, [version]);

  const onGalleryClick = useCallback(
    (picture) => {
      if (picture?.project?._id) {
        if (version) router.query.version = version;
        if (isGalleryPage(router.asPath)) {
          router.query.scroll_to_photo_id = picture._id;
          router.pathname = `/gallery/project/${picture.project.slug}`;
          segmentUtils.sendClickedGalleryPhotoEvent(
            {
              url: router.pathname,
              styles: uniq(
                [
                  ...(picture?.spaces?.map((s) => s?.spaceName) ?? []),
                  ...(picture?.tags?.map((s) => s?.value) ?? []),
                ].filter(Boolean)
              ),
            },
            user,
            {
              context: {
                location: geoLocationInfo?.location,
                ip: geoLocationInfo?.ip,
              },
            }
          );
        } else {
          router.query.project_id = picture.project._id;
          router.query.scroll_to_photo_id = picture._id;
        }
        navigate(router);
      }
    },
    [version, pictures]
  );

  const onGalleryModalClick = useCallback(
    (photo) => {
      if (photo?._id) {
        if (version) router.query.version = version;
        if (isGalleryPage(router.asPath)) {
          router.pathname = `/gallery/project/${photo.project.slug}/photos/${photo._id}`;
        } else {
          router.query.slider_photo_id = photo._id;
          router.query.project_id = photo.project._id;
        }
        navigate(router);
      }
    },
    [version, selectedProjectPics]
  );

  const updateModalActivePic = useCallback((photo) => {
    if (photo) {
      setModalActivePic(photo);
      router.pathname = `/gallery/project/${photo.project.slug}`;
      router.query.scroll_to_photo_id = photo?._id;
      navigate(router);
    }
  }, []);

  // slider util
  const closeSlider = useCallback(() => {
    if (version) router.query.version = version;
    if (isGalleryPage(router.asPath)) {
      router.pathname = `/gallery/project/${modalActivePic?.project?.slug}`;
    } else {
      router.query.slider_photo_id = undefined;
      router.query.project_id = modalActivePic?.project?._id;
    }
    navigate(router);
  }, [version, modalActivePic]);

  const onSliderPhotoChange = useCallback(
    (photo) => {
      if (photo?.project?._id) {
        if (version) router.query.version = version;
        if (isGalleryPage(router.asPath)) {
          router.pathname = `/gallery/project/${photo.project.slug}/photos/${photo._id}`;
        } else {
          router.query.slider_photo_id = photo._id;
          router.query.project_id = modalActivePic?.project?._id;
        }
        navigate(router);
      }
    },
    [version]
  );

  return (
    <>
      <div className="mx-0 my-4 pb-6 md:px-24">
        {title && (
          <div className="flex justify-center">
            <h2 className="mb-8 max-w-[641px] text-center text-4xl font-normal text-accent-700 md:mb-11 md:text-5xl">
              {title}
            </h2>
          </div>
        )}
        {version === "2" ? (
          <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 md:grid-cols-4 md:gap-6 md:px-0">
            {allProjectPics.map((pics, i) => (
              <GalleryCarousel key={pics[0]?.projectId?._id || i} pictures={pics} onClick={onGalleryClick} />
            ))}
          </div>
        ) : (
          <PicturesGrid pictures={paginatedPictures || pictures} onClick={onGalleryClick} />
        )}
      </div>
      <GalleryProjectModal
        mRef={mRef}
        rooms={rooms}
        closeModal={closeModal}
        activePic={modalActivePic}
        setActivePic={updateModalActivePic}
        onPictureClick={onGalleryModalClick}
        picsByRoom={selectedProjectPicsByRoom}
        modalScrollableNode={modalScrollableNode}
      />
      <GalleryPhotoSlider
        onClose={closeSlider}
        activeIndex={sliderIndex}
        photos={selectedProjectPics}
        onScrollEnd={onSliderPhotoChange}
        onSliderArrowClick={onSliderPhotoChange}
      />
    </>
  );
};

Gallery.propTypes = {
  data: PropTypes.shape({
    pictures: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),
    paginatedPictures: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),
  }),
};

export default Gallery;
